import { Injectable, signal, inject, WritableSignal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError, tap } from 'rxjs';
import { Device, Camera } from './bots.component';

export interface CreateDeviceRequest {
  name: string;
  status?: 'online' | 'offline' | 'deactivated';
  cameras?: Camera[];
  imageFile?: File;
  password: string;
}

export interface UpdateCameraRequest {
  id?: string;
  name: string;
}

export interface UpdateDeviceRequest {
  name: string;
  cameras: UpdateCameraRequest[];
  imageFile?: File;
}

@Injectable({
  providedIn: 'root',
})
export class DevicesService {
  private http = inject(HttpClient);

  // Signals
  devices = signal<Device[]>([]);
  isLoading = signal<boolean>(false);
  error = signal<string | null>(null);
  onlineDevices = signal<Device[]>([]);
  remoteDeviceStream: WritableSignal<MediaStream | null> = signal<MediaStream | null>(
    null,
    { debugName: 'remoteDeviceStream' }
  );

  // Current filter state (needed for reloading after create)
  private currentFilter: string = 'all';

  fetchDevices(): void {
    this.isLoading.set(true);
    this.error.set(null);

    this.http
      .get<{ devices: Device[], total: number }>(`/api/devices`)
      .pipe(
        tap((devices: { devices: Device[], total: number }) => {
          this.devices.set(devices.devices);
          this.isLoading.set(false);
          this.error.set(null);
        }),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error?.message || error.message || 'Failed to fetch devices';
          this.error.set(errorMessage);
          this.isLoading.set(false);
          return throwError(() => error);
        })
      )
      .subscribe({
        // Empty handlers - all side effects handled in pipe operators
        // HTTP observables complete automatically, so no subscription cleanup needed
        next: () => {},
        error: () => {} // Error already handled in catchError
      });
  }

  createDevice(deviceData: CreateDeviceRequest): Observable<Device> {
    this.isLoading.set(true);
    this.error.set(null);

    const formData = new FormData();
    formData.append('name', deviceData.name);
    formData.append('password', deviceData.password);
    formData.append('cameras', JSON.stringify(deviceData.cameras ?? []));
    if (deviceData.imageFile) {
      formData.append('file', deviceData.imageFile);
    }

    return this.http.post<Device>(`/api/devices`, formData).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error.error?.message || error.message || 'Failed to create device';
        this.error.set(errorMessage);
        this.isLoading.set(false);
        return throwError(() => error);
      })
    );
  }

  createDeviceAndReload(deviceData: CreateDeviceRequest, currentFilter: string = 'all'): void {
    this.currentFilter = currentFilter;
    this.createDevice(deviceData).subscribe({
      next: () => {
        // Reload devices after successful creation
        this.fetchDevices();
      },
      error: () => {
        // Error already handled in createDevice
        this.isLoading.set(false);
      },
    });
  }

  updateDeviceAndReload(id: string, deviceData: UpdateDeviceRequest): void {
    this.isLoading.set(true);
    this.error.set(null);

    const formData = new FormData();
    formData.append('name', deviceData.name);
    formData.append('cameras', JSON.stringify(deviceData.cameras));
    if (deviceData.imageFile) {
      formData.append('file', deviceData.imageFile);
    }

    this.http.put<void>(`/api/devices/${id}`, formData).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error.error?.message || error.message || 'Failed to update device';
        this.error.set(errorMessage);
        this.isLoading.set(false);
        return throwError(() => error);
      })
    ).subscribe({
      next: () => this.fetchDevices(),
      error: () => this.isLoading.set(false),
    });
  }

  deleteDevice(id: string): void {
    this.isLoading.set(true);
    this.error.set(null);

    this.http.delete<void>(`/api/devices/${id}`).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error.error?.message || error.message || 'Failed to delete device';
        this.error.set(errorMessage);
        this.isLoading.set(false);
        return throwError(() => error);
      })
    ).subscribe({
      next: () => this.fetchDevices(),
      error: () => this.isLoading.set(false),
    });
  }

  getFilteredDevices(filter: string): Device[] {
    const allDevices = this.devices();
    if (filter === 'all') {
      return allDevices;
    }
    return allDevices.filter((device) => device.status === filter);
  }
}

